import React, { useEffect, useRef } from 'react';
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuCheckboxItem, ContextMenuLabel,
  ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent,
} from '../ui/context-menu';
import { datasetMenuItems, runDatasetMenuAction } from '../../lib/layer-menu';

/** Renders a lib/layer-menu item list as Radix context-menu entries (shared by the Layers panel and the map). */
export function MenuItems({ items, entry, onRun }) {
  return items.map((it) => {
    const testId = it.key ? `dsmenu-${it.key}` : undefined;
    if (it.separator) return <ContextMenuSeparator key={it.id} />;
    if (it.header) return <ContextMenuLabel key={it.id} className="px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{it.label}</ContextMenuLabel>;
    if (it.items) {
      return (
        <ContextMenuSub key={it.id}>
          <ContextMenuSubTrigger disabled={it.disabled} data-testid={testId}>{it.label}</ContextMenuSubTrigger>
          <ContextMenuSubContent><MenuItems items={it.items} entry={entry} onRun={onRun} /></ContextMenuSubContent>
        </ContextMenuSub>
      );
    }
    if (it.checked !== undefined) {
      return <ContextMenuCheckboxItem key={it.id} checked={it.checked} onSelect={() => onRun(it.id)}>{it.label}</ContextMenuCheckboxItem>;
    }
    return (
      <ContextMenuItem key={it.id} disabled={it.disabled} onSelect={() => onRun(it.id)} data-menu-id={it.id} data-testid={testId} title={it.title}
        className={it.danger ? 'text-red-400 focus:text-red-400' : undefined}>
        {it.label}
        {it.shortcut && <ContextMenuShortcut>{it.shortcut}</ContextMenuShortcut>}
      </ContextMenuItem>
    );
  });
}

/**
 * The Dataset menu opened by right-clicking an electrode / pseudosection feature on the map.
 * `at` is the cursor position ({ x, y } client pixels): a 1 px trigger is placed there and sent a
 * contextmenu event, so Radix positions the menu at the cursor. `onClose` fires when it closes.
 */
export function DatasetMapMenu({ at, entry, datasetActions, onClose }) {
  const trigger = useRef(null);
  useEffect(() => {
    if (!at || !trigger.current) return;
    trigger.current.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: at.x, clientY: at.y, button: 2 }));
  }, [at, entry]);
  if (!at || !entry) return null;
  return (
    <ContextMenu onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <ContextMenuTrigger asChild>
        <span ref={trigger} aria-hidden="true" style={{ position: 'fixed', left: at.x, top: at.y, width: 1, height: 1, pointerEvents: 'none' }} />
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56" data-testid="dataset-map-menu">
        <MenuItems items={[{ header: true, id: 'hdr', label: `Dataset — ${entry.datasetName || entry.name}` }, ...datasetMenuItems(entry)]} entry={entry}
          onRun={(id) => runDatasetMenuAction(id, entry, datasetActions)} />
      </ContextMenuContent>
    </ContextMenu>
  );
}
